import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { Organization } from 'models/organization';
import { useLabClients } from 'hooks/useLabClients';
import { ClientType, LabClient } from 'models/client-directory';
import Loader from 'components/shared/loader/loader.component';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';
import CustomerGridItem from '../customer-grid-item/customer-grid-item.component';
import { useTranslation } from 'react-i18next';

type SupplierGridProps = {
    initialValue?: Organization;
    isEdit: boolean;
};

const SupplierGrid: FC<SupplierGridProps> = ({ initialValue, isEdit }) => {
    const { t: translate } = useTranslation(['orders']);
    const { order: supplierOrder, updateOrder: updateSupplierOrder } = useContext(SupplierOrderContext);
    const [selectedLab, setSelectedLab] = useState<Partial<Organization>>();
    const [labClients, setLabClients] = useState<LabClient[]>([]);

    const { getLabClients, isLabClientListLoading } = useLabClients(setLabClients, ClientType.SUPPLIER);

    useEffect(() => {
        getLabClients({ variables: { offset: 0, limit: 0 } });
    }, []);

    useEffect(() => {
        setSelectedLab(initialValue);
    }, [initialValue]);

    const handleSelectLab = (lab: Organization) => {
        setSelectedLab(lab);
        updateSupplierOrder({
            ...supplierOrder,
            deliveryDetails: {
                ...supplierOrder.deliveryDetails,
                date: supplierOrder.deliveryDetails?.date || new Date(),
                time: {
                    hour: supplierOrder.deliveryDetails?.time?.hour || undefined,
                    minute: supplierOrder.deliveryDetails?.time?.minute || undefined,
                },
            },
            supplier: lab,
        });
    };

    if (isLabClientListLoading) {
        return <Loader />;
    }

    if (!labClients.length) {
        return (
            <Box mb={3}>
                <Alert severity="info">{translate('orders:NO_SUPPLIER_INFO')}</Alert>
            </Box>
        );
    }

    return (
        <Box mb={4}>
            <Box mb={1}>
                <Typography variant="h2">{translate('orders:CHOOSE_SUPPLIER')}</Typography>
            </Box>
            <Grid container spacing={2} alignItems="stretch">
                {labClients.map((labClient) => {
                    const isSelected = selectedLab?._id === labClient.lab._id;
                    const lab = labClient.lab as Organization;
                    return (
                        <Fragment key={labClient._id}>
                            {(!isEdit || isSelected) && (
                                <CustomerGridItem lab={lab} isSelected={isSelected} handleLabSelect={handleSelectLab} />
                            )}
                        </Fragment>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default SupplierGrid;
